import { useQuery } from '@tanstack/react-query';
import { getGoogleLocationsGeocode } from '../utils/apis';
import type { IHQLocation } from '../schemas/apis/interface';

export function buildHqFallbackAddress(
  hq?: Partial<IHQLocation> | null
): string {
  if (!hq) return '';
  const parts = [
    hq.address_line1,
    hq.city,
    hq.state,
    hq.country,
    hq.zip_code,
  ].filter(p => typeof p === 'string' && p.trim().length > 0);
  return parts.join(', ');
}

function hasValidCoords(lat?: number, lng?: number): boolean {
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
    return false;
  }
  return !(lat === 0 && lng === 0);
}

export function useHqFormattedAddress(hq?: Partial<IHQLocation> | null) {
  const lat = hq?.latitude;
  const lng = hq?.longitude;
  const coordsOk = hasValidCoords(lat, lng);
  const fallback = buildHqFallbackAddress(hq);

  const query = useQuery({
    queryKey: ['hqReverseGeocode', lat, lng],
    queryFn: async () => {
      const res = await getGoogleLocationsGeocode({
        query: `${lat},${lng}`,
      });
      const first = res.data?.results?.[0];
      return first?.formatted_address as string | undefined;
    },
    enabled: coordsOk,
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

  const formattedAddress = coordsOk && query.data ? query.data : fallback;

  return {
    formattedAddress: formattedAddress || '—',
    isLoadingGeocode: coordsOk && query.isPending,
    isFallback: !coordsOk || !query.data,
  };
}
